<template>
  <article class="customer-detail-card financial-account-card">
    <header class="customer-detail-card-header customer-detail-card-header--inline financial-account-card__head">
      <div class="financial-account-card__title-wrap">
        <a
          class="financial-account-card__link"
          :href="detailSummaryHref"
          @click.prevent="openAccount"
        >
          <span class="financial-account-card__badge" :class="`is-${account.code}`">
            {{ account.code.toUpperCase() }}
          </span>
        </a>
        <div>
          <h4>
            <a
              class="financial-account-card__name-link"
              :href="detailSummaryHref"
              @click.prevent="openAccount"
            >
              {{ account.name }}
            </a>
          </h4>
          <p>{{ subtitleText }}</p>
        </div>
      </div>

      <div class="financial-account-card__head-actions">
        <span
          class="app-status-pill"
          :class="account.active ? 'app-status-active' : 'app-status-inactive'"
        >
          {{ account.statusLabel }}
        </span>

        <button
          v-if="showMenu"
          type="button"
          class="financial-account-card__menu-btn"
          title="Opciones"
          @click.stop="toggleMenu"
        >
          <i class="fa fa-ellipsis-v" aria-hidden="true"></i>
        </button>
      </div>

      <div v-if="showMenu && isMenuOpen" class="app-table-actions-menu financial-account-card__menu">
        <button type="button" @click="emitMenuAction('edit')">Editar</button>
        <button v-if="account.active" type="button" @click="emitMenuAction('transfer')">Transferir</button>
        <button type="button" @click="emitMenuAction('toggle-status')">
          {{ account.active ? 'Deshabilitar' : 'Activar' }}
        </button>
      </div>
    </header>

    <div class="financial-account-card__meta">
      <span><strong>{{ text.bankAccounts.card.owner }}:</strong> {{ account.owner }}</span>
      <span><strong>{{ text.bankAccounts.card.branch }}:</strong> {{ account.branch }}</span>
      <span><strong>{{ text.bankAccounts.card.lastSync }}:</strong> {{ account.lastSync }}</span>
    </div>

    <footer class="financial-account-card__footer">
      <div>
        <small>{{ text.summary.labels.available }}</small>
        <strong v-if="account.active">{{ account.balance }}</strong>
        <em v-else>{{ text.summary.labels.inactiveNoBalance }}</em>
      </div>

      <div class="financial-account-card__footer-right">
        <button type="button" class="financial-account-card__movements-btn" @click="$emit('movements', account)">
          {{ text.bankAccounts.card.movements }}
          <i class="fa fa-angle-right" aria-hidden="true"></i>
        </button>
      </div>
    </footer>
  </article>
</template>

<script>
export default {
  name: 'FinancialAccountCard',
  props: {
    text: {
      type: Object,
      required: true,
    },
    account: {
      type: Object,
      required: true,
    },
    showMenu: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['edit', 'transfer', 'toggle-status', 'movements', 'open-account'],
  data() {
    return {
      isMenuOpen: false,
    };
  },
  computed: {
    detailSummaryHref() {
      const accountId = Number(this.account?.id || 0);
      if (accountId <= 0 || !this.$router) {
        return '#';
      }

      return this.$router.resolve({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'summary' },
      }).href;
    },
    subtitleText() {
      const meta = String(this.account?.meta || '').trim();
      if (meta) {
        return meta;
      }

      const maskedNumber = String(this.account?.maskedNumber || '').trim();
      if (!maskedNumber) {
        return '';
      }

      return `${this.text.summary.labels.account} ${maskedNumber}`.trim();
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    openAccount() {
      this.$emit('open-account', this.account);
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    emitMenuAction(action) {
      this.isMenuOpen = false;
      if (action === 'edit') {
        this.$emit('edit', this.account);
        return;
      }

      if (action === 'transfer') {
        if (!this.account?.active) {
          return;
        }
        this.$emit('transfer', this.account);
        return;
      }

      if (action === 'toggle-status') {
        this.$emit('toggle-status', this.account);
      }
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (target && target.closest('.financial-account-card__head')) {
        return;
      }

      this.isMenuOpen = false;
    },
  },
};
</script>

<style scoped>
.financial-account-card {
  border: 1px solid #cfe0f5;
}

.financial-account-card__head {
  position: relative;
}

.financial-account-card__head-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.financial-account-card__title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.financial-account-card__link {
  display: inline-flex;
  text-decoration: none;
}

.financial-account-card__title-wrap h4 {
  margin: 0;
  font-size: 15px;
}

.financial-account-card__name-link {
  color: #0f172a;
  text-decoration: none;
}

.financial-account-card__name-link:hover {
  color: #0b63b4;
  text-decoration: underline;
}

.financial-account-card__title-wrap p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 12px;
}

.financial-account-card__badge {
  min-width: 36px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.financial-account-card__badge.is-san {
  background: #dc2626;
}

.financial-account-card__badge.is-usd {
  background: #7f1d1d;
}

.financial-account-card__badge.is-cc {
  background: #f59e0b;
}

.financial-account-card__badge.is-bb {
  background: #2563eb;
}

.financial-account-card__badge.is-sp {
  background: #7c3aed;
}

.financial-account-card__badge.is-sv {
  background: #0f766e;
}

.financial-account-card__menu-btn {
  border: 0;
  background: transparent;
  color: #64748b;
  width: 28px;
  height: 28px;
  border-radius: 6px;
}

.financial-account-card__menu-btn:hover {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.financial-account-card__menu {
  min-width: 130px;
  top: calc(100% + 4px);
}

.financial-account-card__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #475569;
  font-size: 12px;
}

.financial-account-card__footer {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
}

.financial-account-card__footer small {
  display: block;
  color: #64748b;
  font-size: 11px;
}

.financial-account-card__footer strong {
  color: #0f172a;
  font-size: 18px;
  line-height: 1.05;
}

.financial-account-card__footer em {
  display: block;
  margin-top: 3px;
  font-style: normal;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 600;
}

.financial-account-card__footer-right {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.financial-account-card__movements-btn {
  border: 1px solid transparent;
  background: transparent;
  color: #0070cd;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.financial-account-card__movements-btn:hover {
  background: rgba(0, 112, 205, 0.08);
  border-color: rgba(0, 112, 205, 0.22);
  color: #005eae;
}

@media (max-width: 767px) {
  .financial-account-card__footer {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>